<template>
  <div class="dify-config" v-loading="loading">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span><i class="el-icon-picture-outline"></i> 知识图文工作流</span>
        <el-tag :type="form.enabled ? 'success' : 'info'" size="small">
          {{ form.enabled ? '已启用' : '已停用' }}
        </el-tag>
      </div>

      <el-alert
        title="小程序的一键生知识图文会以 blocking 模式调用此 Dify Workflow；生成完成后，前端再合成右下角分享码。"
        type="info"
        :closable="false"
        show-icon
        class="config-alert"
      />

      <el-form ref="form" :model="form" :rules="rules" label-width="150px" class="config-form">
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="配置名称" prop="name">
              <el-input v-model.trim="form.name" placeholder="请输入配置名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="启用状态">
              <el-switch v-model="form.enabled" active-text="启用" inactive-text="停用" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="Workflow API" prop="apiUrl">
          <el-input v-model.trim="form.apiUrl" placeholder="http(s)://host/v1/workflows/run" />
        </el-form-item>

        <el-form-item label="Dify App Key" prop="apiKey">
          <el-input v-model.trim="form.apiKey" type="password" autocomplete="new-password" placeholder="请输入 Dify App Key" />
        </el-form-item>

        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="生图模型" prop="imageModel">
              <el-select v-model="form.imageModel" class="full-width" @change="handleImageModelChange">
                <el-option label="豆包 Seedream 5.0 Pro" value="豆包 Seedream 5.0 Pro" />
                <el-option label="千问 Qwen-Image 2.0 Pro" value="千问 Qwen-Image 2.0 Pro" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="默认风格" prop="style">
              <el-input v-model.trim="form.style" placeholder="例如：温暖治愈手绘知识卡片" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="千问画布尺寸" prop="size">
              <el-input v-model.trim="form.size" placeholder="例如：1080*1920" :disabled="isDoubaoModel" />
              <span class="field-tip">仅千问 Qwen-Image 使用</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="豆包分辨率" prop="doubaoResolution">
              <el-select v-model="form.doubaoResolution" class="full-width" :disabled="!isDoubaoModel">
                <el-option label="1K" value="1K" />
                <el-option label="2K（更清晰，耗时和费用更高）" value="2K" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="生成数量">
              <el-input-number v-model="form.generateCount" :min="1" :max="6" :disabled="isDoubaoModel" />
              <span class="field-tip">{{ isDoubaoModel ? '豆包每次固定生成 1 张' : '对应 generate_count，支持 1 至 6 张' }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="等待超时">
              <el-input-number v-model="form.timeoutSeconds" :min="30" :max="300" :step="10" />
              <span class="field-tip">秒</span>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="其他要求">
          <el-input
            v-model="form.otherRequirements"
            type="textarea"
            :rows="4"
            placeholder="对应工作流 other_requirements；建议要求右下角预留分享码区域"
          />
        </el-form-item>

        <el-form-item>
          <el-button :loading="loading" @click="loadConfig">重新加载</el-button>
          <el-button type="primary" :loading="saving" @click="handleSave">保存配置</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { getCurrentDifyConfig, saveDifyConfig } from '@/api/system/difyConfig'

const createForm = () => ({
  id: null,
  name: 'Qwen-Image-2.0-Pro 知识图文',
  apiUrl: '',
  apiKey: '',
  style: '温暖治愈手绘知识卡片',
  size: '1080*1920',
  generateCount: 1,
  imageModel: '豆包 Seedream 5.0 Pro',
  doubaoResolution: '2K',
  otherRequirements: '中文清晰完整，不要水印，右下角预留分享码区域',
  timeoutSeconds: 180,
  enabled: true
})

export default {
  name: 'AiDifyConfig',
  data () {
    return {
      loading: false,
      saving: false,
      form: createForm(),
      rules: {
        name: [{ required: true, message: '请输入配置名称', trigger: 'blur' }],
        apiUrl: [
          { required: true, message: '请输入 Workflow API', trigger: 'blur' },
          { pattern: /^https?:\/\//, message: '地址必须以 http:// 或 https:// 开头', trigger: 'blur' }
        ],
        apiKey: [{ required: true, message: '请输入 Dify App Key', trigger: 'blur' }],
        style: [{ required: true, message: '请输入默认风格', trigger: 'blur' }],
        imageModel: [{ required: true, message: '请选择生图模型', trigger: 'change' }],
        size: [{ required: true, message: '请输入千问画布尺寸', trigger: 'blur' }],
        doubaoResolution: [{ required: true, message: '请选择豆包分辨率', trigger: 'change' }]
      }
    }
  },
  created () {
    this.loadConfig()
  },
  computed: {
    isDoubaoModel () {
      return this.form.imageModel === '豆包 Seedream 5.0 Pro'
    }
  },
  methods: {
    handleImageModelChange (model) {
      if (model === '豆包 Seedream 5.0 Pro') this.form.generateCount = 1
    },
    loadConfig () {
      this.loading = true
      return getCurrentDifyConfig()
        .then((data) => {
          this.form = Object.assign(createForm(), data || {})
          this.handleImageModelChange(this.form.imageModel)
          this.$nextTick(() => {
            if (this.$refs.form) this.$refs.form.clearValidate()
          })
        })
        .catch((error) => this.$tip.apiFailed(error))
        .finally(() => {
          this.loading = false
        })
    },
    handleSave () {
      this.$refs.form.validate((valid) => {
        if (!valid) return
        this.saving = true
        saveDifyConfig(this.form)
          .then((data) => {
            this.form = Object.assign(createForm(), data || {})
            this.$tip.success('Dify 配置已保存')
          })
          .catch((error) => this.$tip.apiFailed(error))
          .finally(() => {
            this.saving = false
          })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.dify-config {
  max-width: 1080px;
  margin: 0 auto;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 16px;
  font-weight: 600;
}

.config-alert {
  margin-bottom: 24px;
}

.config-form {
  padding: 4px 24px 0 0;
}

.field-tip {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}

.full-width {
  width: 100%;
}
</style>
